/**
 * Keyboard shortcuts utilities and constants
 */

export type ShortcutCategory =
  | 'navigation'
  | 'calls'
  | 'modals'
  | 'search'
  | 'general';

export interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  description: string;
  category: ShortcutCategory;
  action: () => void;
  disabled?: boolean;
  // If true, shortcut will work even when input is focused
  allowInInput?: boolean;
}

export interface ShortcutHandler {
  id: string;
  shortcuts: KeyboardShortcut[];
  priority?: number; // Higher priority = checked first
}

/**
 * Check if the current focused element is an input field
 */
export const isInputElement = (element: Element | null): boolean => {
  if (!element) return false;

  const tagName = element.tagName.toLowerCase();
  const htmlElement = element as HTMLElement;

  // Check if it's an input-like element
  if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
    return true;
  }

  // Check if it's contentEditable
  // In some environments (like JSDOM), isContentEditable might not work correctly,
  // so we also check the contentEditable attribute
  return htmlElement.isContentEditable || htmlElement.contentEditable === 'true';
};

/**
 * Check if a keyboard event matches a shortcut definition
 */
export const matchesShortcut = (
  event: KeyboardEvent,
  shortcut: KeyboardShortcut,
): boolean => {
  // Normalize the key
  const eventKey = event.key.toLowerCase();
  const shortcutKey = shortcut.key.toLowerCase();

  // Check if keys match
  if (eventKey !== shortcutKey) return false;

  // Check modifier keys - they must match exactly
  const ctrlMatch = !!shortcut.ctrlKey === event.ctrlKey;
  const metaMatch = !!shortcut.metaKey === event.metaKey;
  const shiftMatch = !!shortcut.shiftKey === event.shiftKey;
  const altMatch = !!shortcut.altKey === event.altKey;

  return ctrlMatch && metaMatch && shiftMatch && altMatch;
};

/**
 * Check if a shortcut should be prevented based on current focus
 */
export const shouldPreventShortcut = (shortcut: KeyboardShortcut): boolean => {
  // If shortcut is explicitly allowed in inputs, don't prevent
  if (shortcut.allowInInput) return false;

  // Check if we're in an input element
  const activeElement = document.activeElement;
  if (isInputElement(activeElement)) {
    return true;
  }

  return false;
};

/**
 * Format a shortcut for display (e.g., "Cmd+K" or "Ctrl+K")
 */
export const formatShortcutKeys = (shortcut: KeyboardShortcut): string => {
  const parts: string[] = [];

  // Detect if user is on Mac
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  if (shortcut.ctrlKey) {
    parts.push('Ctrl');
  }

  if (shortcut.metaKey) {
    parts.push(isMac ? 'Cmd' : 'Ctrl');
  }

  if (shortcut.altKey) {
    parts.push(isMac ? 'Option' : 'Alt');
  }

  if (shortcut.shiftKey) {
    parts.push('Shift');
  }

  // Capitalize single letter keys
  const key = shortcut.key.length === 1 ?
    shortcut.key.toUpperCase() :
    shortcut.key;

  parts.push(key);

  return parts.join('+');
};

/**
 * Get the modifier key symbol based on the platform
 */
export const getModifierSymbol = (modifier: 'ctrl' | 'meta' | 'alt' | 'shift'): string => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  if (isMac) {
    switch (modifier) {
      case 'ctrl':
        return '⌃';
      case 'meta':
        return '⌘';
      case 'alt':
        return '⌥';
      case 'shift':
        return '⇧';
    }
  }

  // Windows/Linux symbols
  switch (modifier) {
    case 'ctrl':
    case 'meta':
      return 'Ctrl';
    case 'alt':
      return 'Alt';
    case 'shift':
      return 'Shift';
  }
};
